import { lookupSecurities } from '../../services/apis/securities'
import { SearchSecurityResponse_SecuritySearchItem, Security, SecurityCategory_CategoryCode, securityCategory_CategoryCodeToJSON, SecurityExpression } from '../../proto/generated/security'
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import InfiniteScroll from 'react-infinite-scroll-component'
import './SecuritySearchModal.css'
import { getSecurityKey, getSecuritySymbol } from '../../util/security'
import { cleanUpBadParenthesis, convertExpressionToDisplayExpression } from '../../util/expression'
import SecurityRow from './SecurityRow'
import CompareSecurityRow from './CompareSecurityRow'
import type { CompareSecurityPlacement } from './typings'
import EmptySearch from '../../assets/icons/EmptySearch'
import { Exchange } from '../../proto/generated/common'
import { checkSearchTokensPresence } from '../../util/general'
import { getStrategyIconFromText } from '../../util/strategyBuilder'
import { useSelector } from 'react-redux'
import { selectActiveWidget } from '../../selectors/widgetDashboard'
import { selectActiveLayoutId } from '../../selectors/home'
import { simulatorSecurities } from '../../assets/constants'
import { isFoUnderlyingSearchContext } from '../../util/foUnderlyingSearch'

interface SecurityInfiniteScrollProps {
    categoryCode: number
    instrument?: string
    searchText: string
    expressions: SecurityExpression[]
    onExpressionDeleted: any
    cachedSecurities: RefObject<Security[]>
    nameToExpression: RefObject<Map<string, string>>
    expressionToName: RefObject<Map<string, string>>
    cachedStrategyExpressions: RefObject<SecurityExpression[]>
    highlightSelected: boolean
    onSecuritySelect?: any
    selectedItems?: Set<string>
    tailIcon?: boolean
    deleteIcon?: boolean
    focusedIndex: number
    expressionMode: boolean
    onMaxFocusableChange: (max: number) => void
    /** Preview text in the search box (used while arrowing through rows). */
    setInputValue: (value: string) => void
    showOnlySimulatorSecurities: boolean
    showOnlyFOSecurities?: boolean
    watchlistMode?: boolean
    compareSecurityPicker?: boolean
    onCompareSecurityPick?: (security: Security, placement: CompareSecurityPlacement) => void
    comparePickerSubmitRef?: RefObject<(() => void) | null>
}

const SecurityInfiniteScroll = ({
    categoryCode,
    instrument,
    searchText,
    expressions: userExpressions,
    cachedSecurities,
    cachedStrategyExpressions,
    onSecuritySelect,
    onExpressionDeleted,
    highlightSelected,
    tailIcon,
    deleteIcon,
    expressionMode,
    setInputValue,
    onMaxFocusableChange,
    expressionToName,
    nameToExpression,
    focusedIndex,
    selectedItems = new Set(),
    showOnlySimulatorSecurities,
    showOnlyFOSecurities,
    watchlistMode,
    compareSecurityPicker,
    onCompareSecurityPick,
    comparePickerSubmitRef
}: SecurityInfiniteScrollProps) => {
    const pageSize = 20
    const pageNumber = useRef<number>(0)
    const previousPageStrategyExpressions = useRef<SecurityExpression[]>([])
    const previousPageSecurities = useRef<Security[]>([])
    const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>(1000)
    const totalItemsRef = useRef<number>(1000)
    const [securities, setSecurities] = useState<Security[]>(categoryCode === SecurityCategory_CategoryCode.SPREADS ? [] : cachedSecurities.current)
    const [nothingMatched, setNothingMatched] = useState<boolean>(false)
    const selectedSecurities = Array.from(selectedItems)
    const [strategySearch, setStrategySearch] = useState<boolean>(false)
    const [strategyExpressions, setStrategyExpressions] = useState<SecurityExpression[]>(
        [SecurityCategory_CategoryCode.SPREADS, SecurityCategory_CategoryCode.ALL].includes(categoryCode) ? cachedStrategyExpressions.current : []
    )
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))

    const fetchSecurities = useCallback(
        (categoryCode: number, searchText: string, pageNumber: number, pageSize: number) => {
            if (showOnlySimulatorSecurities) {
                previousPageStrategyExpressions.current = []
                previousPageSecurities.current = simulatorSecurities
                setStrategyExpressions(previousPageStrategyExpressions.current)
                setSecurities(previousPageSecurities.current)
            } else {
                lookupSecurities({
                    searchText: searchText,
                    categoryCode: securityCategory_CategoryCodeToJSON(categoryCode),
                    pageNumber: pageNumber,
                    pageSize: pageSize,
                    ...(instrument ? { instrument } : {}),
                    hasFo: isFoUnderlyingSearchContext({
                        showOnlyFOSecurities,
                        watchlistMode,
                        widgetType: activeWidget?.widgetType,
                    })
                        ? true
                        : false,
                })
                    .then((response) => {
                        const responseExpressions = response.securitySearchItems
                            .filter((item: SearchSecurityResponse_SecuritySearchItem) => item.isExpression && item.expression)
                            .map((item) => item.expression!)
                        const responseSecurities = response.securitySearchItems
                            .filter((item: SearchSecurityResponse_SecuritySearchItem) => !item.isExpression && item.securities.length)
                            .map((item) => item.securities[0])
                        previousPageStrategyExpressions.current = pageNumber === 0 ? responseExpressions : previousPageStrategyExpressions.current.concat(responseExpressions || [])
                        previousPageSecurities.current = pageNumber === 0 ? responseSecurities : previousPageSecurities.current.concat(responseSecurities || [])
                        if (response.totalCount !== totalItemsRef.current) {
                            totalItemsRef.current = response.totalCount
                            setTotalNumberOfItems(response.totalCount)
                        }
                        if (pageNumber === 0) {
                            cachedSecurities.current = responseSecurities
                            cachedStrategyExpressions.current = responseExpressions
                            if (response.securitySearchItems.length && response.securitySearchItems[0].isExpression) {
                                setStrategySearch(true)
                            } else {
                                setStrategySearch(false)
                            }
                        }
                        setNothingMatched(pageNumber === 0 && (!responseSecurities || responseSecurities.length === 0) && (!responseExpressions || responseExpressions.length === 0))

                        setStrategyExpressions(previousPageStrategyExpressions.current)
                        setSecurities(previousPageSecurities.current)
                    })
                    .catch((e) => {
                        console.error(e)
                    })
            }
        },
        [activeWidget?.widgetType, cachedSecurities, cachedStrategyExpressions, instrument, showOnlyFOSecurities, showOnlySimulatorSecurities, watchlistMode]
    )

    const fetchNextPage = useCallback(() => {
        pageNumber.current += 1
        fetchSecurities(categoryCode, searchText, pageNumber.current, pageSize)
    }, [categoryCode, fetchSecurities, searchText])

    // Category / type changes: clear immediately and refetch (no remount key → no layout flash).
    useEffect(() => {
        pageNumber.current = 0
        previousPageSecurities.current = []
        previousPageStrategyExpressions.current = []
        setSecurities([])
        setStrategyExpressions([])
        setNothingMatched(false)
        fetchSecurities(categoryCode, searchText, 0, pageSize)
        // searchText intentionally omitted — debounced below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryCode, instrument, fetchSecurities])

    // Typing: debounce refetch so the list does not thrash on every keystroke.
    // Skip the initial mount — category/instrument effect already loads the first page.
    const skipSearchDebounceOnce = useRef(true)
    useEffect(() => {
        if (skipSearchDebounceOnce.current) {
            skipSearchDebounceOnce.current = false
            return
        }
        const timeoutId = setTimeout(() => {
            pageNumber.current = 0
            fetchSecurities(categoryCode, searchText, 0, pageSize)
        }, 200)
        return () => {
            if (timeoutId) clearTimeout(timeoutId)
        }
        // categoryCode/instrument handled above
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, fetchSecurities])

    useEffect(() => {
        strategyExpressions.forEach((exp) => {
            const displayExpression = cleanUpBadParenthesis(convertExpressionToDisplayExpression(exp.expression, exp.securities))
            expressionToName.current.set(displayExpression, exp.name)
            nameToExpression.current.set(exp.name, displayExpression)
        })
    }, [strategyExpressions])

    const filteredUserExpressions = useMemo(() => {
        return userExpressions.filter(
            (item) =>
                (categoryCode === SecurityCategory_CategoryCode.ALL || categoryCode === SecurityCategory_CategoryCode.SPREADS) &&
                (checkSearchTokensPresence(searchText.trim().split(' '), item.expression.toLowerCase()) || checkSearchTokensPresence(searchText.trim().split(' '), item.name.toLowerCase()))
        )
    }, [userExpressions, categoryCode, searchText])

    const strategyExpressionsLength = useMemo(() => strategyExpressions.length, [strategyExpressions])
    const securitiesLength = useMemo(() => securities.length, [securities])
    const filteredUserExpressionsLength = useMemo(() => filteredUserExpressions.length, [filteredUserExpressions])

    useEffect(() => {
        onMaxFocusableChange(filteredUserExpressionsLength + securitiesLength + strategyExpressionsLength)
    }, [filteredUserExpressionsLength, securitiesLength, strategyExpressionsLength])

    useEffect(() => {
        if (!comparePickerSubmitRef || !compareSecurityPicker || !onCompareSecurityPick) {
            if (comparePickerSubmitRef) comparePickerSubmitRef.current = null
            return
        }
        comparePickerSubmitRef.current = () => {
            let secIndex = -1
            if (strategySearch) {
                const off = filteredUserExpressionsLength + strategyExpressionsLength
                if (focusedIndex >= off) secIndex = focusedIndex - off
            } else if (focusedIndex < securitiesLength) {
                secIndex = focusedIndex
            }
            if (secIndex < 0 || secIndex >= securitiesLength) return
            onCompareSecurityPick(securities[secIndex], 'same_percentage_scale')
        }
        return () => {
            comparePickerSubmitRef.current = null
        }
    }, [
        comparePickerSubmitRef,
        compareSecurityPicker,
        onCompareSecurityPick,
        focusedIndex,
        strategySearch,
        filteredUserExpressionsLength,
        strategyExpressionsLength,
        securitiesLength,
        securities
    ])

    const filteredUserExpressionsView = useMemo(() => {
        return filteredUserExpressions.map((expression, index) => {
            const focused = index + (strategySearch ? 0 : securities.length) === focusedIndex
            return (
                <SecurityRow
                    exchange={expression.securities[0]?.exchange ?? 0}
                    symbol={expression.name}
                    colorSymbol={expression.securities.reduce((acc, curr) => acc + `${curr.symbol ?? ''} `, '')}
                    displayName={String(convertExpressionToDisplayExpression(expression.expression, expression.securities) ?? '')}
                    selected={selectedSecurities.find((sec) => sec === expression.expression) !== undefined}
                    categoryCode={SecurityCategory_CategoryCode.SPREADS}
                    key={`${expression.id}-${index}`}
                    highlightSelected={highlightSelected}
                    searchTerm={searchText}
                    isExpression={true}
                    focused={focused}
                    expressionMode={expressionMode}
                    onSecuritySelect={(selected: boolean, focused: boolean) => {
                        if (focused) {
                            const displayExp = cleanUpBadParenthesis(convertExpressionToDisplayExpression(expression.expression, expression.securities))
                            setInputValue(expressionToName.current.get(displayExp) ?? displayExp)
                        } else {
                            onSecuritySelect(expression, selected, focused, 'EXPRESSION')
                        }
                    }}
                    onSecurityDeleted={() => onExpressionDeleted(expression.expression)}
                    tailIcon={tailIcon}
                    deleteIcon={deleteIcon}
                />
            )
        })
    }, [
        filteredUserExpressions,
        strategySearch,
        securities.length,
        focusedIndex,
        selectedSecurities,
        highlightSelected,
        searchText,
        tailIcon,
        deleteIcon,
        setInputValue,
        expressionToName,
        onSecuritySelect,
        onExpressionDeleted,
        expressionMode
    ])

    const strategyExpressionsView = useMemo(() => {
        return strategyExpressions.map((expression, index) => {
            const focused = index + filteredUserExpressions.length + (strategySearch ? 0 : securities.length) === focusedIndex
            return (
                <SecurityRow
                    exchange={expression.securities[0].exchange}
                    symbol={expression.name}
                    colorSymbol={expression.securities.reduce((acc, curr) => acc + `${curr.symbol ?? ''} `, '')}
                    displayName={String(convertExpressionToDisplayExpression(expression.expression, expression.securities) ?? '')}
                    selected={selectedSecurities.find((sec) => sec === expression.expression) !== undefined}
                    categoryCode={SecurityCategory_CategoryCode.SPREADS}
                    key={`${expression.expression}-${index}`}
                    searchTerm={searchText}
                    isExpression={true}
                    focused={focused}
                    expressionMode={expressionMode}
                    highlightSelected={highlightSelected}
                    logo={getStrategyIconFromText(expression.name)}
                    onSecuritySelect={(selected: boolean, focused: boolean) => {
                        if (focused) {
                            const displayExp = cleanUpBadParenthesis(convertExpressionToDisplayExpression(expression.expression, expression.securities))
                            setInputValue(expressionToName.current.get(displayExp) ?? displayExp)
                        } else {
                            onSecuritySelect(expression, selected, focused, 'STRATEGY_EXPRESSION')
                        }
                    }}
                    tailIcon={tailIcon}
                    deleteIcon={undefined}
                />
            )
        })
    }, [
        strategyExpressions,
        filteredUserExpressions.length,
        strategySearch,
        securities.length,
        focusedIndex,
        selectedSecurities,
        searchText,
        highlightSelected,
        tailIcon,
        setInputValue,
        expressionToName,
        onSecuritySelect,
        expressionMode
    ])

    return (
        <>
            {nothingMatched ? (
                <Stack className={'empty-view'} justifyContent={'center'} alignItems={'center'} direction={'column'} spacing={1}>
                    <EmptySearch />
                    <Typography variant={'body1'}>No symbols match your criteria... </Typography>
                </Stack>
            ) : (
                <InfiniteScroll
                    dataLength={filteredUserExpressionsLength + strategyExpressionsLength + securitiesLength}
                    next={fetchNextPage}
                    height={700}
                    hasMore={strategyExpressionsLength + securitiesLength < totalNumberOfItems}
                    loader={totalNumberOfItems > strategyExpressionsLength + securitiesLength && <Typography sx={{ textAlign: 'center' }}></Typography>}
                >
                    {strategySearch && filteredUserExpressionsView}
                    {strategySearch && strategyExpressionsView}
                    {securities.map((security, index) => {
                        const focused = index + (strategySearch ? filteredUserExpressions.length + strategyExpressions.length : 0) === focusedIndex
                        if (compareSecurityPicker && onCompareSecurityPick) {
                            return (
                                <CompareSecurityRow
                                    key={`${security.code}-${index}`}
                                    security={security}
                                    searchTerm={searchText}
                                    focused={focused}
                                    onPick={(placement) => onCompareSecurityPick(security, placement)}
                                />
                            )
                        }
                        return (
                            <SecurityRow
                                series={security.series}
                                exchange={security.exchange}
                                symbol={security.symbol || ''}
                                displayName={security.displayName || ''}
                                categoryCode={security.categoryCode || SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN}
                                instrument={security.instrument}
                                logoUrl={security.logoUrl}
                                onSecuritySelect={(selected: boolean, focused: boolean) => {
                                    if (focused && !expressionMode) {
                                        setInputValue(getSecuritySymbol(security.exchange ?? Exchange.NSE, security.symbol || ''))
                                    } else if (!focused || expressionMode) {
                                        onSecuritySelect(security, selected, focused, 'SECURITY')
                                    }
                                }}
                                key={`${security.code}-${index}`}
                                focused={focused}
                                expressionMode={expressionMode}
                                highlightSelected={highlightSelected}
                                selected={selectedSecurities.find((sec) => sec === getSecurityKey(security.exchange, security.code)) !== undefined}
                                searchTerm={searchText}
                                tailIcon={tailIcon}
                                deleteIcon={deleteIcon}
                            />
                        )
                    })}
                    {!strategySearch && filteredUserExpressionsView}
                    {!strategySearch && strategyExpressionsView}
                </InfiniteScroll>
            )}
        </>
    )
}

export default SecurityInfiniteScroll
